<script setup>
import { useLayout } from '@/components/layouts/composables/layout';
import { onBeforeMount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const { layoutState, setActiveMenuItem, toggleMenu } = useLayout();

const props = defineProps({
    item: {
        type: Object,
        default: () => ({})
    },
    index: {
        type: Number,
        default: 0
    },
    root: {
        type: Boolean,
        default: true
    },
    parentItemKey: {
        type: String,
        default: null
    }
});

const isActiveMenu = ref(false);
const itemKey = ref(null);

onBeforeMount(() => {
    itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);

    const activeItem = layoutState.activeMenuItem;

    // Check if this menu item should be active based on the current route
    const isRouteActive = checkActiveRoute(props.item);

    isActiveMenu.value = activeItem === itemKey.value ||
        (activeItem && activeItem.startsWith(itemKey.value + '-')) ||
        isRouteActive;
});

watch(
    [() => layoutState.activeMenuItem, () => route.path],
    ([newVal, newPath]) => {
        const isRouteActive = checkActiveRoute(props.item);
        isActiveMenu.value = newVal === itemKey.value ||
            (newVal && newVal.startsWith(itemKey.value + '-')) ||
            isRouteActive;
    }
);

function itemClick(event, item) {
    if (item.disabled) {
        event.preventDefault();
        return;
    }

    if ((item.to || item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
        toggleMenu();
    }

    if (item.command) {
        item.command({ originalEvent: event, item: item });
    }

    const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey) : itemKey.value;

    setActiveMenuItem(foundItemKey);
}

function checkActiveRoute(item) {
    if (!item.to) {
        // If this item has no direct route but has children, check if any child is active
        if (item.items && item.items.length > 0) {
            return item.items.some(child => checkActiveRoute(child));
        }
        return false;
    }

    // Exact match for root path "/"
    if (item.to === '/') {
        return route.path === '/';
    }

    // For other routes, check if current path starts with the menu item path
    // This handles sub-routes like /users/123, /users/new, etc.
    return route.path === item.to || route.path.startsWith(item.to + '/');
}
</script>

<template>
    <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
        <div v-if="root && item.visible !== false" class="layout-menuitem-root-text">{{ item.label }}</div>
        <a v-if="(!item.to || item.items) && item.visible !== false" :href="item.url"
            @click="itemClick($event, item, index)" :class="item.class" :target="item.target" tabindex="0">
            <i :class="item.icon" class="layout-menuitem-icon"></i>
            <span class="layout-menuitem-text">{{ item.label }}</span>
            <i v-if="item.suffixIcon" :class="item.suffixIcon" class="ml-1 text-xs opacity-60"></i>
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
        </a>
        <router-link v-if="item.to && !item.items && item.visible !== false" @click="itemClick($event, item, index)"
            :class="[item.class, { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="item.to">
            <i :class="item.icon" class="layout-menuitem-icon"></i>
            <span class="layout-menuitem-text">{{ item.label }}</span>
            <i v-if="item.suffixIcon" :class="item.suffixIcon" class="ml-1 text-xs opacity-60"></i>
            <i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
        </router-link>
        <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
            <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
                <main-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child"
                    :parentItemKey="itemKey" :root="false"></main-menu-item>
            </ul>
        </Transition>
    </li>
</template>

<style lang="scss" scoped></style>
